import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const { t } = useLang();
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get('session_id');
  const [state, setState] = useState({ phase: 'polling', data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', data: null, error: t('pay_no_session') });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState({ phase: 'paid', data, error: null });
          return;
        }
        if (data.status === 'expired') {
          setState({ phase: 'expired', data, error: null });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: 'timeout', data, error: null });
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({ phase: 'error', data: null, error: err?.response?.data?.detail || err.message });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, t]);

  return (
    <Layout>
      <div className="max-w-xl mx-auto bg-card-steel border-2 border-gold/30 rounded-lg p-8 text-center" data-testid="payment-success-page">
        {state.phase === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-4 animate-spin" />
            <h1 className="font-h uppercase text-3xl text-white mb-2">{t('pay_verifying')}</h1>
            <p className="text-gray-400">{t('pay_verifying_sub')}</p>
          </>
        )}
        {state.phase === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-4" />
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('pay_thanks_tag')}</div>
            <h1 className="font-h uppercase text-3xl text-white mb-2">{t('pay_success_title')}</h1>
            <p className="text-gray-300 mb-1">{t('pay_success_plan')}: <span className="text-gold font-h uppercase">{state.data?.plan_id}</span></p>
            <p className="text-gray-400 text-sm mb-6">${state.data?.amount?.toFixed(2)} {state.data?.currency?.toUpperCase()}</p>
            <Link
              to="/dashboard"
              data-testid="pay-success-cta"
              className="inline-block bg-gold text-navy hover:bg-gold-light font-h uppercase tracking-wider px-6 py-3 rounded-md font-semibold transition"
            >
              {t('pay_go_dashboard')}
            </Link>
          </>
        )}
        {state.phase === 'expired' && (
          <>
            <AlertTriangle className="w-12 h-12 text-gold mx-auto mb-4" />
            <h1 className="font-h uppercase text-3xl text-white mb-2">{t('pay_expired_title')}</h1>
            <p className="text-gray-400 mb-6">{t('pay_expired_sub')}</p>
            <Link to="/pricing" className="inline-block border-2 border-gold/60 text-gold hover:bg-gold/10 font-h uppercase tracking-wider px-6 py-3 rounded-md font-semibold">
              {t('pay_back_pricing')}
            </Link>
          </>
        )}
        {state.phase === 'timeout' && (
          <>
            <AlertTriangle className="w-12 h-12 text-gold mx-auto mb-4" />
            <h1 className="font-h uppercase text-3xl text-white mb-2">{t('pay_timeout_title')}</h1>
            <p className="text-gray-400 mb-6">{t('pay_timeout_sub')}</p>
            <Link to="/pricing" className="inline-block border-2 border-gold/60 text-gold hover:bg-gold/10 font-h uppercase tracking-wider px-6 py-3 rounded-md font-semibold">
              {t('pay_back_pricing')}
            </Link>
          </>
        )}
        {state.phase === 'error' && (
          <>
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-h uppercase text-3xl text-white mb-2">{t('pay_error_title')}</h1>
            <p className="text-gray-400 mb-2">{state.error}</p>
            <Link to="/pricing" className="inline-block mt-4 border-2 border-gold/60 text-gold hover:bg-gold/10 font-h uppercase tracking-wider px-6 py-3 rounded-md font-semibold">
              {t('pay_back_pricing')}
            </Link>
          </>
        )}
      </div>
    </Layout>
  );
}
